import { Group, LoadingOverlay, Select, Space, Stack } from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';
import { PageTitle } from 'components/PageTitle';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect, useState } from 'react';
import { isDefined, isNullish } from 'remeda';
import { useLoadAgedBalance, useLoadAgedBalanceCompact } from 'shared/hooks/use-load-aged-balance';
import {
  type AgedBalanceOverTime,
  useLoadAgedBalanceOverTime,
} from 'shared/hooks/use-load-aged-balance-over-time';
import { useLoadViewsV2 } from 'shared/hooks/use-load-views-v2';

import { AnalyticsCard, UpdatedAt } from '../components';
import {
  AgedBalanceCompactChart,
  AgedBalanceOverTimeChart,
  AgedBalanceTable,
} from '../components/charts';

export const AgedBalance = () => {
  const { t } = useTranslation();

  const [selectedViewId, setSelectedViewId] = useLocalStorage<string | null>({
    key: 'aged-balance-view',
    defaultValue: null,
  });

  const { views: debtorViews } = useLoadViewsV2({
    resourceType: 'debtors',
    onSuccess: views => {
      if (isNullish(selectedViewId)) setSelectedViewId(views[0].id);
    },
  });

  useEffect(() => {
    if (isDefined(debtorViews) && isNullish(selectedViewId)) setSelectedViewId(debtorViews[0].id);
  }, [debtorViews]);

  const { agedBalanceCompact, isAgedBalanceCompactLoading } = useLoadAgedBalanceCompact({
    viewId: selectedViewId,
  });

  const { agedBalance, isAgedBalanceLoading } = useLoadAgedBalance({
    viewId: selectedViewId,
  });

  const { agedBalanceOverTime, isAgedBalanceOverTimeLoading } = useLoadAgedBalanceOverTime({
    viewId: selectedViewId,
  });

  const [agedBalanceVersion, setAgedBalanceVersion] = useState<'compact' | 'overTime'>('overTime');

  // If there are too few dates for the balance over time to display nicely, fallback to compact balance
  useEffect(() => {
    if (!isAgedBalanceOverTimeLoading && agedBalanceOverTime != null) {
      dispatchChartComponent(agedBalanceOverTime);
    }
  }, [isAgedBalanceOverTimeLoading, agedBalanceOverTime]);

  const dispatchChartComponent = (_agedBalanceOverTime: AgedBalanceOverTime) => {
    const amountOfDatesPresent = Object.keys(_agedBalanceOverTime.data).length;
    const shouldFallbackToCompactBalance = amountOfDatesPresent < 2;
    if (shouldFallbackToCompactBalance) setAgedBalanceVersion('compact');
  };

  const chartComponent =
    agedBalanceVersion === 'compact' ? (
      <AnalyticsCard title={t(i18nKeys.ANALYTICS.AGED_BALANCE.TITLE)}>
        <LoadingOverlay visible={isAgedBalanceCompactLoading} />
        <AgedBalanceCompactChart agedBalanceCompact={agedBalanceCompact} />
      </AnalyticsCard>
    ) : (
      <AgedBalanceOverTimeChart selectedViewId={selectedViewId} />
    );

  return (
    <div>
      <PageTitle overrideTitle={t(i18nKeys.ANALYTICS.AGED_BALANCE.TITLE)}>
        <PageTitle.Back />
        <Group align="center" justify="center">
          <Stack gap={0}>
            {t(i18nKeys.ANALYTICS.AGED_BALANCE.TITLE)}
            <UpdatedAt />
          </Stack>
        </Group>
        <PageTitle.Actions>
          <Select
            label={t(i18nKeys.FILTER)}
            onChange={setSelectedViewId}
            allowDeselect={false}
            value={selectedViewId}
            data={debtorViews?.map(({ id, name }) => ({ label: name, value: id })) ?? []}
          />
        </PageTitle.Actions>
      </PageTitle>
      {chartComponent}
      <Space h="md" />
      <AgedBalanceTable agedBalance={agedBalance} isLoading={isAgedBalanceLoading} />
    </div>
  );
};
